// The setup screen's pure layer helpers: the forms a transaction has, the
// columns that can decide, keys, the tenant body a draft is saved as, the
// locks a standard (design) entry and a published column carry, and the plain
// sentences the screen shows ("Customer type is Export and Item type is …").
//
// No React, no I/O — test-script/test_uda_setup_ui.mjs runs this file under
// node. The tenant-may vocabularies are twins of uda/model.py
// (COLUMN_/SET_/FLAG_TENANT_MAY_KEYS); the same test compares them with Python.

import type { ColumnDef, SqlModel, TransactionModel } from '../../txn/types'
import type {
  UdaColumn, UdaColumnType, UdaDriver, UdaForm, UdaLayer, UdaRule, UdaRuleValue, UdaSet,
} from '../types'
import { INDEXED_TEXT_MAX, KEY_MAX_LENGTH, MAX_DRIVER_VALUES } from '../limits'
import { ANY, canonValue, driverKeys, isExcept } from '../rules'

// ── the tenant-may vocabularies (twins of uda/model.py) ─────────────────────

/** ColumnDef keys a designer may let a tenant change on a STANDARD column
 *  (the designer's own vocabulary, verbatim): every attribute ColumnDef key
 *  except identity / storage (`name`, `col_type`, `secure`) and the
 *  structural ones, plus `pictures`. */
export const COLUMN_TENANT_MAY_KEYS: readonly string[] = [
  'heading', 'list_heading', 'descr', 'default_value', 'protect', 'mandatory', 'min_length',
  'db_size', 'width', 'x', 'y', 'screen_x', 'hidden', 'list_hidden', 'format', 'text_case',
  'hide_label', 'inline_with', 'edit_mask', 'lookup', 'lookup_filter', 'lookup_filter_rules',
  'lookup_sql', 'lookup_title', 'lookup_add_object', 'lookup_add_lines', 'lookup_sql_data_col',
  'lookup_xref', 'lookup_resolve', 'lookup_multi', 'item_change', 'validations', 'display',
  'pictures',
]
export const SET_TENANT_MAY_KEYS: readonly string[] = ['label', 'color', 'placement', 'rules', 'hidden']
export const FLAG_TENANT_MAY_KEYS: readonly string[] = ['name', 'icon', 'color', 'when', 'show_on', 'hidden']

/** A column's leaves that are fixed once it is PUBLISHED (values may exist):
 *  its identity, its type and its encryption (policy: "retire it and add a
 *  new column"). Edit-mask data codes are fixed too (see `publishedCodes`). */
export const PUBLISHED_FIXED_LEAVES: readonly string[] = ['name', 'col_type', 'secure']

/** The key every layer key must match (policy `_KEY_RE`). */
export const KEY_RE = new RegExp(`^[a-z][a-z0-9_]{0,${KEY_MAX_LENGTH - 1}}$`)

export const ORIGIN_DESIGN = 'design'
export const ORIGIN_TENANT = 'tenant'

// ── small helpers ───────────────────────────────────────────────────────────

export function clone<T>(v: T): T {
  return v === undefined ? v : JSON.parse(JSON.stringify(v))
}

function stable(v: unknown): string {
  if (v === undefined) return 'u'
  if (v === null || typeof v !== 'object') return JSON.stringify(v)
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`
  const o = v as Record<string, unknown>
  return `{${Object.keys(o).filter(k => o[k] !== undefined).sort()
    .map(k => `${JSON.stringify(k)}:${stable(o[k])}`).join(',')}}`
}

export function sameValue(a: unknown, b: unknown): boolean {
  const blankish = (x: unknown) => x === undefined || x === null || x === ''
    || (Array.isArray(x) && x.length === 0)
  if (blankish(a) && blankish(b)) return true
  return stable(a) === stable(b)
}

export const truthy = (v: unknown): boolean => v === true || v === 1
  || (typeof v === 'string' && ['1', 'true', 'yes', 'y', 'on'].includes(v.trim().toLowerCase()))

/** `CUSTOMER_TYPE` → "Customer type". */
export function humanize(name: string): string {
  const words = String(name || '').trim().replace(/[_\s]+/g, ' ').toLowerCase().split(' ').filter(Boolean)
  if (!words.length) return ''
  const s = words.join(' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** A layer key from free text, unique among `taken` (`new_column_2`, …). */
export function slugKey(text: string, taken: Iterable<string>, fallback = 'item'): string {
  const used = new Set([...taken].map(t => String(t).toLowerCase()))
  let base = String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  if (!base || !/^[a-z]/.test(base)) base = `${fallback}${base ? `_${base}` : ''}`
  base = base.slice(0, KEY_MAX_LENGTH)
  if (!used.has(base)) return base
  for (let i = 2; i < 10000; i++) {
    const suffix = `_${i}`
    const k = `${base.slice(0, KEY_MAX_LENGTH - suffix.length)}${suffix}`
    if (!used.has(k)) return k
  }
  return base
}

// ── the transaction's forms ─────────────────────────────────────────────────

export interface SetupForm {
  formNo: string
  title: string
  table: string
  /** The parent form ('' for the header). */
  parentFormNo: string
  isHeader: boolean
  columns: ColumnDef[]
  /** The form's own column groups, in first-seen order. */
  groups: string[]
  sm: SqlModel
}

function formKey(c: { name?: string; db_name?: string }): string {
  return String(c.name || c.db_name || '').trim().replace(/\s+/g, '_').toLowerCase()
}

/** The transaction's forms as the setup screen lists them. */
export function formsOf(model: TransactionModel | null | undefined): SetupForm[] {
  const out: SetupForm[] = []
  const wrappers = model?.sql_models || []
  let header = ''
  wrappers.forEach((w, i) => {
    const sm = w?.sql_model
    if (!sm) return
    const raw = (sm as unknown as { form_no?: string | number }).form_no
    const formNo = raw != null && String(raw).trim() !== '' ? String(raw).trim() : String(i + 1)
    if (!header) header = formNo
    const columns = (sm.columns || []).map(x => x?.column).filter(Boolean) as ColumnDef[]
    const groups: string[] = []
    for (const c of columns) {
      const g = String(c.group_name || '').trim()
      if (g && !groups.includes(g)) groups.push(g)
    }
    const parentRaw = String(sm.parent_form_no ?? '').trim()
    const isHeader = formNo === header
    out.push({
      formNo, title: String(sm.form_title || `Form ${formNo}`), table: String(sm.main_table || ''),
      parentFormNo: isHeader ? '' : (parentRaw && parentRaw !== formNo ? parentRaw : header),
      isHeader, columns, groups, sm,
    })
  })
  return out
}

export function columnByKey(form: SetupForm | null | undefined, key: string): ColumnDef | null {
  const want = String(key || '').trim().toLowerCase()
  return form?.columns.find(c => formKey(c) === want || String(c.db_name || '').toLowerCase() === want) || null
}

const SPEC_RE = /^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*,\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)\s*$/

/** `TABLE(DISPLAY, DATA)` → its parts (the engine's parse_lookup_spec shape).
 *  An advanced `lookup_sql` lookup has none — it cannot decide. */
export function parseLookup(col: ColumnDef | null | undefined): { table: string; display: string; data: string } | null {
  if (!col || String(col.lookup_sql || '').trim()) return null
  const m = String(col.lookup || '').trim().match(SPEC_RE)
  return m ? { table: m[1], display: m[2], data: m[3] } : null
}

/** The form's columns with a table lookup (a master a driver can go through). */
export function lookupColumns(form: SetupForm | null | undefined): ColumnDef[] {
  return (form?.columns || []).filter(c => !!parseLookup(c) && !c.uda)
}

/** The form's columns with a value list (a form driver). */
export function valueListColumns(form: SetupForm | null | undefined): ColumnDef[] {
  return (form?.columns || []).filter(c => {
    const m = c.edit_mask?.mask
    return !c.uda && (m === 'ddlb' || m === 'radio') && (c.edit_mask?.values || []).length > 0
  })
}

// ── layer access ────────────────────────────────────────────────────────────

export function emptyLayer(): UdaLayer {
  return { version: 1, forms: {} }
}

export function emptyForm(): UdaForm {
  return { enabled: true, group: '', drivers: [], sets: [], flags: [] }
}

export function formOf(layer: UdaLayer | null | undefined, formNo: string): UdaForm | null {
  return (layer?.forms || {})[formNo] || null
}

/** Every attribute key on a form (all sets) plus the form's own column keys —
 *  a new key must differ from all of them. */
export function takenKeys(form: UdaForm | null, setup: SetupForm | null): string[] {
  const out: string[] = []
  for (const s of form?.sets || []) for (const c of s.columns || []) out.push(c.name)
  for (const c of setup?.columns || []) out.push(formKey(c), formKey(c).replace(/^uda_/, ''))
  return out
}

export function driverLabel(d: UdaDriver | null | undefined): string {
  if (!d) return ''
  if (d.label) return d.label
  const f = d.from
  return humanize(f.kind === 'master' ? f.column : f.column) || d.key
}

export function driverPath(d: UdaDriver): string {
  const f = d.from
  if (f.kind === 'master') {
    return `${f.via} → ${f.column}`
  }
  return `form.${f.column}`
}

/** Whether a driver reads `via` + `column` (or a form column). */
export function sameDriverSource(d: UdaDriver, from: UdaDriver['from']): boolean {
  const a = d.from
  if (a.kind !== from.kind) return false
  if (a.kind === 'master' && from.kind === 'master') {
    return a.via.toLowerCase() === from.via.toLowerCase()
      && a.column.toLowerCase() === from.column.toLowerCase()
  }
  return a.column.toLowerCase() === (from as { column: string }).column.toLowerCase()
}

/** A new set with every driver at Any (prototype `addcombo`). */
export function newSet(form: UdaForm, label: string): UdaSet {
  const key = slugKey(label, form.sets.map(s => s.key), 'set')
  const rule: UdaRule = {}
  for (const k of driverKeys(form.drivers)) rule[k] = ANY
  const used = new Set(form.sets.map(s => Number(s.color)).filter(Number.isFinite))
  let color = 0
  while (used.has(color) && color < 64) color++
  return { key, label, color, placement: { group: null }, rules: [rule], columns: [], origin: ORIGIN_TENANT }
}

/** A new text column (prototype `addcol`). */
export function newColumn(taken: string[]): UdaColumn {
  let n = 1
  while (taken.includes(`new_column_${n}`)) n++
  return {
    name: `new_column_${n}`, heading: `New column ${n}`, descr: '', col_type: 'VARCHAR', db_size: '40',
    mandatory: false, protect: false, hidden: false, edit_mask: { mask: '', values: [] },
    default_value: '', item_change: null, validations: [], in_grid: false, origin: ORIGIN_TENANT,
  }
}

// ── who gets a set, in words ────────────────────────────────────────────────

export type LabelOf = (driverKey: string, code: string) => string

function summ(codes: string[], key: string, labelOf: LabelOf, max = 3): string {
  const ls = codes.map(c => labelOf(key, c))
  return ls.length <= max ? ls.join(', ') : `${ls.slice(0, max).join(', ')} +${ls.length - max}`
}

/** "Customer type is Export and Item type is Apparel, Footwear" ('' = Any). */
export function ruleText(drivers: UdaDriver[], rule: UdaRule, labelOf: LabelOf): string {
  const parts: string[] = []
  for (const d of drivers) {
    const v = canonValue(rule[d.key])
    if (v === ANY) continue
    if (isExcept(v)) parts.push(`${driverLabel(d)} is not ${summ(v.any_except, d.key, labelOf)}`)
    else parts.push(`${driverLabel(d)} is ${summ(v as string[], d.key, labelOf)}`)
  }
  return parts.join(' and ')
}

export const WHEN_NOBODY = 'Nobody yet'
export const WHEN_ALWAYS = 'Any value (always asked)'

/** Prototype `whenText`. */
export function whenText(drivers: UdaDriver[], set: UdaSet, labelOf: LabelOf): string {
  const rules = set.rules || []
  if (!rules.length) return WHEN_NOBODY
  if (rules.length === 1) return ruleText(drivers, rules[0], labelOf) || WHEN_ALWAYS
  return rules.map(r => ruleText(drivers, r, labelOf) || 'any value').join(', or ')
}

/** The label a picker button shows for one rule value ("Any", "Apparel, +2"). */
export function valueSummary(v: UdaRuleValue | undefined, key: string, labelOf: LabelOf): string {
  const c = canonValue(v)
  if (c === ANY) return 'Any'
  if (isExcept(c)) return `All but ${summ(c.any_except, key, labelOf, 2)}`
  return summ(c as string[], key, labelOf, 2) || 'Nothing picked'
}

// ── the tenant body a draft is saved as (layers.py merge, inverted) ────────

const ENTRY_META = ['origin', 'tenant_may']

function stripMeta<T extends Record<string, unknown>>(entry: T): T {
  const out = clone(entry) as Record<string, unknown>
  for (const k of ENTRY_META) delete out[k]
  if (Array.isArray(out.columns)) {
    out.columns = (out.columns as Array<Record<string, unknown>>).map(c => stripMeta(c))
  }
  return out as T
}

function patchOf(eff: Record<string, unknown>, des: Record<string, unknown>, idKey: string,
  leaves: readonly string[] | null): Record<string, unknown> | null {
  const out: Record<string, unknown> = { [idKey]: eff[idKey] }
  const keys = leaves || [...new Set([...Object.keys(eff), ...Object.keys(des)])]
  let changed = false
  for (const k of keys) {
    if (k === idKey || ENTRY_META.includes(k) || k === 'columns') continue
    if (!sameValue(eff[k], des[k])) { out[k] = eff[k] === undefined ? null : clone(eff[k]); changed = true }
  }
  return changed ? out : null
}

/**
 * The TENANT body that, merged over `design` (layers.merge_layers), gives the
 * edited `effective` layer: additions in full, and for a standard entry only
 * the leaves that differ from the design (the policy judges whether each may).
 * Standard drivers are never re-sent; a standard form's own settings stay the
 * designer's.
 */
export function tenantBodyOf(effective: UdaLayer, design: UdaLayer | null | undefined): UdaLayer {
  const out: UdaLayer = { version: effective.version || 1, forms: {} }
  const dForms = design?.forms || {}
  for (const [formNo, ef] of Object.entries(effective.forms || {})) {
    const df = dForms[formNo]
    if (!df) {
      out.forms[formNo] = stripMeta(ef as unknown as Record<string, unknown>) as unknown as UdaForm
      continue
    }
    const tf: UdaForm = { drivers: [], sets: [], flags: [] }
    const dDrv = new Set((df.drivers || []).map(d => d.key))
    for (const d of ef.drivers || []) if (!dDrv.has(d.key)) tf.drivers.push(stripMeta(d as unknown as Record<string, unknown>) as unknown as UdaDriver)
    const dSets = new Map((df.sets || []).map(s => [s.key, s]))
    for (const s of ef.sets || []) {
      const ds = dSets.get(s.key)
      if (!ds) { tf.sets.push(stripMeta(s as unknown as Record<string, unknown>) as unknown as UdaSet); continue }
      const ps = patchOf(s as unknown as Record<string, unknown>, ds as unknown as Record<string, unknown>,
        'key', SET_TENANT_MAY_KEYS) || { key: s.key }
      const dCols = new Map((ds.columns || []).map(c => [c.name, c]))
      const cols: Record<string, unknown>[] = []
      for (const c of s.columns || []) {
        const dc = dCols.get(c.name)
        if (!dc) { cols.push(stripMeta(c as unknown as Record<string, unknown>)); continue }
        const pc = patchOf(c as unknown as Record<string, unknown>, dc as unknown as Record<string, unknown>, 'name', null)
        if (pc) cols.push(pc)
      }
      if (Object.keys(ps).length > 1 || cols.length) tf.sets.push({ ...(ps as unknown as UdaSet), columns: cols as unknown as UdaColumn[] })
    }
    const dFlags = new Map((df.flags || []).map(f => [f.key, f]))
    for (const f of ef.flags || []) {
      const dfl = dFlags.get(f.key)
      if (!dfl) { tf.flags.push(stripMeta(f as unknown as Record<string, unknown>) as unknown as UdaForm['flags'][number]); continue }
      const pf = patchOf(f as unknown as Record<string, unknown>, dfl as unknown as Record<string, unknown>, 'key', FLAG_TENANT_MAY_KEYS)
      if (pf) tf.flags.push(pf as unknown as UdaForm['flags'][number])
    }
    if (tf.drivers.length || tf.sets.length || tf.flags.length) out.forms[formNo] = tf
  }
  return out
}

// ── locks ───────────────────────────────────────────────────────────────────

export type SetupMode = 'tenant' | 'design'

export function isStandard(entry: { origin?: string } | null | undefined): boolean {
  return entry?.origin === ORIGIN_DESIGN
}

/** The leaves a person may change on an entry, or null for "all of them".
 *  Tenant mode: a standard entry only its `tenant_may` leaves. Design mode:
 *  everything (the designer owns the layer). */
export function editableLeaves(mode: SetupMode, entry: { origin?: string; tenant_may?: string[] } | null | undefined,
  vocabulary: readonly string[]): Set<string> | null {
  if (mode === 'design' || !isStandard(entry)) return null
  const allowed = new Set(vocabulary)
  return new Set((entry?.tenant_may || []).map(t => String(t).trim().toLowerCase()).filter(t => allowed.has(t)))
}

export function canEdit(leaves: Set<string> | null, leaf: string): boolean {
  return leaves === null || leaves.has(leaf)
}

/** Columns of the PUBLISHED layer by `form|name`, with their edit-mask data
 *  codes — what is fixed once values may exist. */
export function publishedIndex(published: UdaLayer | null | undefined): Map<string, { codes: string[]; col: UdaColumn }> {
  const out = new Map<string, { codes: string[]; col: UdaColumn }>()
  for (const [formNo, f] of Object.entries(published?.forms || {})) {
    for (const s of f.sets || []) {
      for (const c of s.columns || []) {
        const codes = (c.edit_mask?.values || []).map(v => String(v?.data ?? '').trim()).filter(Boolean)
        out.set(`${formNo}|${c.name}`, { codes, col: c })
      }
    }
  }
  return out
}

// ── storage, as the setup screen explains it ───────────────────────────────

export function typeOf(types: UdaColumnType[] | null | undefined, colType: string | undefined): UdaColumnType | null {
  const want = String(colType || '').trim().toUpperCase()
  if (!want) return null
  return (types || []).find(t => t.name === want)
    || (types || []).find(t => (t.aliases || []).map(a => a.toUpperCase()).includes(want)) || null
}

/** Which `twasta_uda_values` slot holds a column (the engine's per-column
 *  choice: numbers / booleans → value_num, dates → value_date, JSON and
 *  checkbox groups → value_json, text in value_text when it can never exceed
 *  the indexed width, else value_json). */
export function storedSlot(col: UdaColumn, types: UdaColumnType[] | null | undefined): string {
  const t = typeOf(types, col.col_type)
  const cat = t?.category || 'string'
  if (col.edit_mask?.mask === 'checkbox') return 'value_json'
  if (['integer', 'decimal', 'float', 'boolean'].includes(cat)) return 'value_num'
  if (['date', 'datetime'].includes(cat)) return 'value_date'
  if (cat === 'json') return 'value_json'
  if (t?.size_kind === 'length') {
    const n = Number(String(col.db_size || '').split(',')[0])
    return Number.isFinite(n) && n > 0 && n <= INDEXED_TEXT_MAX && !col.secure ? 'value_text' : 'value_json'
  }
  return cat === 'string' && t?.size_kind !== 'none' ? 'value_text' : 'value_json'
}

/** A column as the runtime renders it (`uda_<name>` in the set's group). */
export function asColumnDef(c: UdaColumn, group: string): ColumnDef {
  const key = `uda_${c.name}`
  return {
    col_id: key, group_name: group, heading: c.heading || c.name, descr: c.descr || '', key: false,
    default_value: c.default_value || '', protect: c.protect ?? false, mandatory: c.mandatory ?? false,
    db_name: key.toUpperCase(), table_name: '', db_size: c.db_size || '', width: Number(c.width) || 0,
    x: '', y: '', hidden: c.hidden ?? false, format: c.format || '', lookup: c.lookup || '',
    item_change: null, validations: [], ...c,
    name: key,
    edit_mask: c.edit_mask || { mask: '', values: [] },
    col_type: c.col_type || 'VARCHAR',
  } as ColumnDef
}

/** An inert form model holding one set's columns ("How it looks"). */
export function setPreviewModel(set: UdaSet, formTitle: string, group?: string): SqlModel {
  const g = group || set.label || set.key
  return {
    form_title: formTitle, form_descr: '', form_no: '0', main_table: '',
    columns: (set.columns || []).filter(c => !c.retired).map(c => ({ column: asColumnDef(c, g) })),
  } as unknown as SqlModel
}

/** Whether a master column may decide: at most MAX_DRIVER_VALUES values. */
export function decidable(total: number | null | undefined): boolean {
  return typeof total === 'number' && total > 0 && total <= MAX_DRIVER_VALUES
}
